import React, {useState, useEffect} from 'react';
import {useParams, useLocation, useNavigate} from 'react-router';
import {useQuery} from 'react-query';
import {connect} from 'react-redux';
import {DashboardLayout, Loader} from 'components';
import Breadcrumbs from 'components/BreadCrumbs';
import {PAGES_URL} from 'helpers/constants';
import {fetchProfileDetails, fetchSearchDetails} from 'requests/queries/aml';
import AMLSidebar from './AMLSidebar';
import {useAMLContext} from 'helpers/context/AMLContext';
import * as Actions from 'store/actions';
import * as sanitizeHtml from 'sanitize-html';

const PROFILE_IGNORE = ['relations', 'related_entities', 'articles'];

function AMLScreeningSingleResult({team = {}, getTeamMembers}) {
  const {id} = useParams();
  const {state} = useLocation();
  const navigate = useNavigate();
  const {setCustomerName, setSearchDetails} = useAMLContext();
  const members = team?.members || {team: []};
  const searchProfiles = state?.searchProfiles || {profiles: []};

  const rawStateCustomerName = state?.customerName || 'Unknown';
  const customerName =
    typeof rawStateCustomerName === 'string'
      ? rawStateCustomerName.replaceAll('_', ' ')
      : rawStateCustomerName;
  const searchId = state?.searchId;
  const [activeProfileId, setActiveProfileId] = useState(id);
  const [expandedArticles, setExpandedArticles] = useState({});

  useEffect(() => {
    getTeamMembers(`status=active`);
  }, [getTeamMembers]);

  useEffect(() => {
    setCustomerName(customerName);
  }, [customerName, setCustomerName]);

  const {isLoading: isSearchLoading} = useQuery({
    queryKey: ['searchDetails', searchId],
    queryFn: () => fetchSearchDetails({id: searchId}),
    enabled: !!searchId,
    onSuccess: data => {
      setSearchDetails(data);
      setCustomerName(data?.customerName || customerName || 'Unknown');
    },
    cacheTime: 0,
    staleTime: 0,
    retry: false,
  });

  const {
    data: profile,
    isLoading: isProfileLoading,
    error: profileError,
  } = useQuery({
    queryKey: ['profileDetails', activeProfileId],
    queryFn: () => fetchProfileDetails({id: activeProfileId}),
    enabled: !!activeProfileId,
  });

  const getProfileTypeStyles = profileType => {
    const type = profileType?.toLowerCase() || 'unknown';
    const typeMap = {
      pep: {bg: 'bg-[#E2F5F6]', text: 'text-[#4A878D]'},
      adverse_media: {bg: 'bg-[#D9E5F8]', text: 'text-[#2A5392]'},
      sanction: {bg: 'bg-[#F0D3D1]', text: 'text-[#B42318]'},
    };
    return typeMap[type] || {bg: 'bg-[#F7F7F7]', text: 'text-gray-700'};
  };

  const handleProfileClick = profileId => {
    setActiveProfileId(profileId);
    navigate(`/aml-screening/v2/single-result/${profileId}`, {
      state: {searchProfiles, customerName, searchId},
    });
  };

  const displayType = profileType => {
    const typeLower = profileType?.toLowerCase();
    let result = profileType;

    if (typeLower === 'sanction') result = 'SANCTIONS & WARNINGS';
    else if (typeLower === 'adverse_media') result = 'ADVERSE MEDIA';
    else if (typeLower === 'pep') result = 'PEP';

    if (typeof result === 'string') {
      return result.replaceAll('_', ' ');
    }
    return result;
  };

  const fullDetails = data =>
    Object.entries(data || {})
      .filter(([key]) => !PROFILE_IGNORE.includes(key))
      .map(([key, value]) => {
        let displayValue = value;
        if (Array.isArray(value)) {
          displayValue = value
            .map(item =>
              typeof item === 'string' ? item.replaceAll('_', ' ') : item,
            )
            .join(', ');
        } else if (typeof value === 'string') {
          displayValue = value.replaceAll('_', ' ');
        } else if (typeof value === 'boolean') {
          displayValue = value.toString();
        }

        return {
          title: key.replaceAll('_', ' ').toUpperCase(),
          value: displayValue || 'N/A',
        };
      })
      .filter(Boolean);

  const truncateText = (text, wordLimit = 35) => {
    if (!text) return '';
    const words = text.split(' ');
    if (words.length <= wordLimit) return text;
    return `${words.slice(0, wordLimit).join(' ')}...`;
  };

  const countWords = text => {
    if (!text) return 0;
    return text.split(' ').filter(word => word.length > 0).length;
  };

  const toggleArticle = (source, index) => {
    setExpandedArticles(prev => ({
      ...prev,
      [`${source}-${index}`]: !prev[`${source}-${index}`],
    }));
  };

  return (
    <DashboardLayout
      bg="bg-white80"
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">AML Screening</p>
      }
    >
      <div className="flex items-center min-h-[52px] py-2 flex-wrap gap-3 justify-between mr-[-24px] ml-[-32px] px-8 border border-transparent border-b-[#E4E3E6]">
        <Breadcrumbs
          items={[
            {label: 'AML Screening', path: PAGES_URL.AML_SCREENING},
            {
              label: 'AML Screening Results',
              path: `/aml-screening/v2/results/${searchId}`,
              state: {customerName, searchProfiles, searchId},
            },
            {label: `${customerName}`},
          ]}
        />
      </div>
      <div className="flex flex-col lg:flex-row w-full pt-3 gap-[54px] items-start">
        <div className="w-full lg:w-[67%]">
          <div className="flex flex-col md:flex-row gap-5">
            <div className="md:w-[30%] w-full">
              <div className="rounded-xl h-fit px-3 py-[22px] flex flex-col gap-4">
                {searchProfiles.profiles.length ? (
                  searchProfiles.profiles.map(profile => {
                    const {bg: typeBg, text: typeText} = getProfileTypeStyles(
                      profile.profile_type,
                    );
                    return (
                      <div
                        key={profile.id}
                        onClick={() => handleProfileClick(profile.id)}
                        className={`py-[18px] bg-white px-3 rounded-xl cursor-pointer flex flex-col gap-2 ${
                          activeProfileId === profile.id
                            ? 'border-[0.5px] text-[#0B1C56] border-brandBlue'
                            : 'text-[#080917]'
                        }`}
                      >
                        <p className="text-sm font-medium">
                          {profile.profile_name
                            ? profile.profile_name.replaceAll('_', ' ')
                            : 'Unknown'}
                        </p>
                        <div
                          className={`py-1 px-2 rounded-full w-fit ${typeBg} ${typeText} text-[10px] uppercase font-normal`}
                        >
                          {displayType(profile.profile_type) || 'N/A'}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-gray-500">
                    No profiles available.
                  </p>
                )}
              </div>
            </div>
            <div className="md:w-[70%] w-full">
              {isProfileLoading ? (
                <div className="w-full bg-white h-full rounded-lg">
                  <Loader height={50} />
                </div>
              ) : profileError ? (
                <div className="text-red-500 py-4">
                  Error loading profile: {profileError.message}
                </div>
              ) : !profile ? (
                <div className="text-gray-500 py-4">No profile found.</div>
              ) : (
                <div className="bg-white py-9 px-8">
                  <p className="text-base font-normal text-[#080917] mb-6">
                    {profile?.profile?.name
                      ? typeof profile.profile.name === 'string'
                        ? profile.profile.name.replaceAll('_', ' ')
                        : Array.isArray(profile.profile.name) &&
                          profile.profile.name.length > 0
                        ? profile.profile.name[0].replaceAll('_', ' ')
                        : 'Unknown'
                      : 'Unknown'}
                  </p>
                  <div>
                    {fullDetails(profile.profile).map(
                      ({title, value}, index) => (
                        <div
                          key={index}
                          className="flex lg:flex-row flex-col lg:items-center justify-between w-full gap-2 py-4 border-[0.5px] border-x-transparent border-y-[#CECED2]"
                        >
                          <p className="text-[#080917] text-[10px] font-medium uppercase">
                            {title}
                          </p>
                          <p className="text-body text-sm font-normal capitalize text-left lg:text-right">
                            {value}
                          </p>
                        </div>
                      ),
                    )}
                  </div>
                  <div className="p-4 bg-[#F9F9F9] rounded mt-6">
                    <p className="text-base font-normal text-[#080917] flex items-center gap-1.5 mb-4">
                      Relationships
                      <span className="bg-brandBlue rounded-full w-4 h-4 flex items-center justify-center text-white text-[10px] py-0.5 px-1">
                        {profile?.profile?.relations?.[0]
                          ? Object.keys(profile.profile.relations[0]).length
                          : 0}
                      </span>
                    </p>
                    <div className="flex flex-col gap-4">
                      {profile?.profile?.relations?.length ? (
                        profile?.profile?.relations?.map(
                          (relationship, index) => (
                            <div key={index} className="flex flex-col gap-2">
                              {Object.entries(relationship || {}).map(
                                ([key, value]) => (
                                  <div
                                    key={key}
                                    className="py-[10px] px-4 flex flex-col gap-2 rounded-lg bg-white"
                                  >
                                    <p className="text-sm font-normal text-[#080917] capitalize">
                                      {(typeof value === 'string'
                                        ? value.replaceAll('_', ' ')
                                        : value) || 'N/A'}
                                    </p>
                                    <p className="text-[10px] font-medium text-body uppercase">
                                      {key}
                                    </p>
                                  </div>
                                ),
                              )}
                            </div>
                          ),
                        )
                      ) : (
                        <p className="text-sm text-gray-500">
                          No relationships found.
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="p-4 bg-[#F9F9F9] rounded mt-6">
                    <div className="space-y-2">
                      <p className="text-base font-normal text-[#080917] flex items-center gap-1.5 mb-4">
                        Source
                      </p>
                      {profile.profile?.articles?.length ? (
                        profile.profile.articles.map((article, index) => {
                          let processedBody = sanitizeHtml(article.body || '', {
                            allowedTags: [],
                            allowedAttributes: {},
                          });
                          if (typeof processedBody === 'string') {
                            processedBody = processedBody.replaceAll('_', ' ');
                          }

                          const wordCount = countWords(processedBody);
                          const isExpanded =
                            expandedArticles[`${article.source}-${index}`];
                          const displayText = isExpanded
                            ? processedBody
                            : truncateText(processedBody);

                          return (
                            <div
                              key={index}
                              className="mt-2 space-y-2 p-4 rounded-lg bg-white"
                            >
                              <p className="text-[10px] text-[#4A4A68] font-normal mb-1">
                                {article.timestamp
                                  ? new Date(article.timestamp)
                                      .toISOString()
                                      .slice(0, 10)
                                      .slice(2)
                                  : 'N/A'}
                              </p>
                              <p className="text-[#080917] text-sm mb-2">
                                {article.headline
                                  ? article.headline.replaceAll('_', ' ')
                                  : 'No title'}
                              </p>
                              <div
                                className="text-[10px] font-normal text-[#73739B] mb-2 whitespace-pre-line"
                                dangerouslySetInnerHTML={{
                                  __html: displayText,
                                }}
                              />
                              {wordCount > 35 && (
                                <button
                                  onClick={() =>
                                    toggleArticle(article.source, index)
                                  }
                                  className="text-[10px] font-normal text-[#73739B] underline underline-offset-2"
                                >
                                  {isExpanded ? 'Show Less' : 'Read More'}
                                </button>
                              )}
                              <a
                                href={article.source}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[10px] font-normal underline underline-offset-2 text-[#3F7CDB] block truncate"
                              >
                                {article.source || 'No URL'}
                              </a>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm text-gray-500">
                          No articles available.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <AMLSidebar isSearchLoading={isSearchLoading} members={members} />
      </div>
    </DashboardLayout>
  );
}

export default connect(
  state => ({
    team: state.team,
  }),
  {getTeamMembers: Actions.getTeamMembers},
)(AMLScreeningSingleResult);
